import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { TranslatePipe } from "@ngx-translate/core";
import { OvertimePeriodKey, OvertimeRateColumn } from "../../../../../models/properties/OvertimeRate";
import { SalesOffice } from "../../../../../models/properties/SalesOffices";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { CommonDataService } from "../../../../../services/common-data.service";
import { EstimateSettings, LaborRates, OverTimeAttributes } from "../../../../../models/dtos";
import { MultilingualCurrencyPipe } from "../../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { IOvertimeRatesFormGroup } from "../../../../../models/properties/IOvertimeRatesFormGroup";
import { IOvertimeRateRowFormGroup } from "../../../../../models/properties/IOvertimeRateRowFormGroup";
import { NumericFieldConfig } from "../../../../../shared/numeric/numeric.types";
import { NumericMaskDirective } from "../../../../../shared/numeric/numeric-mask.directive";

/** Keys of LaborRates that hold a plain numeric base rate. */
type LaborRateNumericKey = {
    [K in keyof LaborRates]: LaborRates[K] extends number ? K : never
}[keyof LaborRates];

interface OvertimeLaborDefinition {
    labor: string;
    baseRateField: LaborRateNumericKey;
    overtimeRateField: keyof OverTimeAttributes;
}

/**
 * Single source of truth for the rows of the overtime table: the labor code, the base rate it
 * displays, and the OverTimeAttributes field its three editable columns write back to.
 */
const OVERTIME_LABOR_DEFINITIONS: readonly OvertimeLaborDefinition[] = [
    { labor: 'PM', baseRateField: 'projectManagementRates', overtimeRateField: 'overtimePMRate' },
    { labor: 'ENG', baseRateField: 'engineeringRates', overtimeRateField: 'overtimeENGRate' },
    { labor: 'CENG', baseRateField: 'cyberSecEngineeringRates', overtimeRateField: 'overtimeCENGRate' },
    { labor: 'EENG', baseRateField: 'energyEngineeringRates', overtimeRateField: 'overtimeEENGRate' },
    { labor: 'LENG', baseRateField: 'lEngineeringRates', overtimeRateField: 'overtimeLENGRate' },
    { labor: 'NENG', baseRateField: 'networkEngineeringRates', overtimeRateField: 'overtimeNENGRate' },
    { labor: 'QENG', baseRateField: 'qualificationEngineeringRates', overtimeRateField: 'overtimeQENGRate' },
    { labor: 'GRAP', baseRateField: 'graphicsRates', overtimeRateField: 'overtimeGrapRate' },
    { labor: 'PROG', baseRateField: 'programmingRates', overtimeRateField: 'overtimeProgRate' },
    { labor: 'SPEC', baseRateField: 'specialistRates', overtimeRateField: 'overtimeSPECRate' },
    { labor: 'DDS', baseRateField: 'ddsRates', overtimeRateField: 'overtimeDdsRate' },
    { labor: 'MECH', baseRateField: 'mechanicalRates', overtimeRateField: 'overtimeMECHRate' },
    { labor: 'ELEC', baseRateField: 'electricianRates', overtimeRateField: 'overtimeELECRate' }
];

const OVERTIME_PERIOD_BY_COLUMN: Record<OvertimeRateColumn, OvertimePeriodKey> = {
    oneDotOneX: 'overTime11X',
    oneDotFiveX: 'overTime15X',
    twoX: 'overTime2X'
};

const HIDE_WHEN_WBS_SELECTED_LABORS = new Set(['PM', 'EENG', 'LENG', 'MECH', 'ELEC']);
const WBS_OT_VISIBLE_LABORS = new Set(['CENG', 'QENG', 'GRAP', 'PROG']);
const WBS_DDEP_VISIBLE_LABORS = new Set(['NENG', 'DDS']);

@Component({
    selector: 'app-sol-properties-overtime-rates',
    templateUrl: './sol-properties-overtime-rates.component.html',
    styleUrls: ['./sol-properties-overtime-rates.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatTableModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        TranslatePipe,
        MultilingualCurrencyPipe,
        NumericMaskDirective
    ]
})
export class SolPropertiesOvertimeRatesComponent implements OnInit {

    wbsOtRateVisible: boolean = false;
    wbsDdepOtRateVisible: boolean = false;
    displayedColumns: string[] = ['labor', 'base', 'oneDotOneX', 'oneDotFiveX', 'twoX'];
    overtimeRates = new MatTableDataSource<FormGroup<IOvertimeRateRowFormGroup>>([]);

    salesOffices: SalesOffice[] = [];

    overtimeRatesForm: FormGroup<IOvertimeRatesFormGroup> = new FormGroup<IOvertimeRatesFormGroup>({
        salesOffice: new FormControl<number | null>(null),
        overrideCalculation: new FormControl<boolean>({ value: false, disabled: true }, { nonNullable: true }),
        rates: new FormArray<FormGroup<IOvertimeRateRowFormGroup>>([])
    });
    salesOfficeLaborRate: LaborRates[] = [];
    estimateSettings: EstimateSettings;
    private isReadOnly: boolean = false;
    private readonly destroyRef = inject(DestroyRef);

    readonly otRatesNumericFieldsConfig : NumericFieldConfig = {
        kind: 'currency',
        scale: CONSTANTS.TWO,
        min: CONSTANTS.ZERO,
        max: CONSTANTS.OVERTIME_RATES_MAX_VALUE,
        useGrouping: true,
        showStepper: false,
        step: CONSTANTS.ONE,
        revertToLastValidValue: true,
    }

    constructor(
        private readonly commmonDataService: CommonDataService,
        private readonly solPropertiesService: SolPropertiesService
    ){}

    ngOnInit(): void {
        this.estimateSettings = this.commmonDataService.getEstimateMetaData().estimateSettings;
        this.setVisibilityOfLabor();
        this.getSalesOfficeLaborData();
        this.setupReadOnlyMode();
        this.restoreSelectedSalesOffice();

        this.subscribeSalesOfficeValueChanges();
        this.subscribeOverrideCalculationValueChanges();
    }

    get ratesFormArray(): FormArray<FormGroup<IOvertimeRateRowFormGroup>> {
        return this.overtimeRatesForm.controls.rates;
    }

    private setupReadOnlyMode(): void {
        this.commmonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                this.isReadOnly = isReadOnly;
                if (isReadOnly) {
                    this.overtimeRatesForm.disable({ emitEvent: false });
                } else {
                    this.overtimeRatesForm.enable({ emitEvent: false });
                    this.overtimeRatesForm.controls.overrideCalculation.disable({ emitEvent: false });
                }
                this.applyRateRowsEditability();
            });
    }

    private subscribeSalesOfficeValueChanges(): void {
        this.overtimeRatesForm.controls.salesOffice.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((salesOfficeId: number | null) => {
            this.solPropertiesService.overtimeRatesFormState = { salesOffice: salesOfficeId };
            this.applySalesOffice(salesOfficeId);
        });
    }

    private subscribeOverrideCalculationValueChanges(): void {
        this.overtimeRatesForm.controls.overrideCalculation.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((isChecked: boolean) => {
            const selectedSalesOfficeId = this.overtimeRatesForm.controls.salesOffice.value;
            if (selectedSalesOfficeId === null || selectedSalesOfficeId === undefined) {
                return;
            }

            this.solPropertiesService.setOvertimeOverride(selectedSalesOfficeId, isChecked);
            this.solPropertiesService.markPropertiesChanged();

            if (isChecked) {
                this.applyRateRowsEditability();
            } else {
                // Switching the override off restored the calculated rates on the staged model,
                // so the rows are rebuilt to bring those values back into view.
                this.buildRateRows(selectedSalesOfficeId);
            }
        });
    }


    setVisibilityOfLabor(): void {
        if(this.commmonDataService.globalPPMmode){
            this.wbsOtRateVisible = true;
            this.wbsDdepOtRateVisible = true;
        }
    }

    private restoreSelectedSalesOffice(): void {
        const savedSalesOffice = this.solPropertiesService.overtimeRatesFormState?.salesOffice;
        const isSavedSalesOfficeAvailable = savedSalesOffice !== null && savedSalesOffice !== undefined
            && this.salesOffices.some(office => office.id === savedSalesOffice);
        
        const salesOfficeId = isSavedSalesOfficeAvailable 
            ? savedSalesOffice 
            : this.getValidSalesOfficeOrFirst(this.estimateSettings.salesOfficeId);

        this.overtimeRatesForm.controls.salesOffice.setValue(salesOfficeId, { emitEvent: false });
        this.solPropertiesService.overtimeRatesFormState = { salesOffice: salesOfficeId };
        this.applySalesOffice(salesOfficeId);
    }

    private getValidSalesOfficeOrFirst(officeId: number): number | null {
        if (officeId > CONSTANTS.ZERO && this.salesOffices.some(office => office.id === officeId)) {
            return officeId;
        }
        return this.salesOffices[CONSTANTS.ZERO]?.id ?? null;
    }

    private applySalesOffice(salesOfficeId: number | null): void {
        if (salesOfficeId === null) {
            this.clearRateRows();
            return;
        }

        const isOverridden = this.solPropertiesService.isOvertimeOverrideEnabled(salesOfficeId);
        this.overtimeRatesForm.controls.overrideCalculation.setValue(isOverridden, { emitEvent: false });
        this.buildRateRows(salesOfficeId);
    }

    private buildRateRows(salesOfficeId: number): void {
        const selectedSalesOfficeRate = this.salesOfficeLaborRate.find(
            (element: LaborRates) => element.salesOfficeId === salesOfficeId
        );

        if (!selectedSalesOfficeRate) {
            this.clearRateRows();
            return;
        }

        this.setVisibilityOfLaborForSalesOffice(salesOfficeId);
        const showHideWhenWbsSelectedRows = this.shouldShowHideWhenWbsSelectedRows();
        const ratesFormArray = this.ratesFormArray;
        ratesFormArray.clear({ emitEvent: false });

        for (const definition of OVERTIME_LABOR_DEFINITIONS) {
            if (!this.isLaborRowVisible(definition.labor, showHideWhenWbsSelectedRows)) {
                continue;
            }
            ratesFormArray.push(this.createRateRow(selectedSalesOfficeRate, definition), { emitEvent: false });
        }

        this.overtimeRates.data = [...ratesFormArray.controls];
        this.applyRateRowsEditability();
    }

    private createRateRow(laborRates: LaborRates, definition: OvertimeLaborDefinition): FormGroup<IOvertimeRateRowFormGroup> {
        const overtimeRates = laborRates.stdOverTimeRates;
        const overtimeRateField = definition.overtimeRateField;

        return new FormGroup<IOvertimeRateRowFormGroup>({
            labor: new FormControl<string>(definition.labor, { nonNullable: true }),
            base: new FormControl<number>(laborRates[definition.baseRateField] ?? CONSTANTS.ZERO, { nonNullable: true }),
            oneDotOneX: new FormControl<number>(overtimeRates?.overTime11X?.[overtimeRateField] ?? CONSTANTS.ZERO, { nonNullable: true }),
            oneDotFiveX: new FormControl<number>(overtimeRates?.overTime15X?.[overtimeRateField] ?? CONSTANTS.ZERO, { nonNullable: true }),
            twoX: new FormControl<number>(overtimeRates?.overTime2X?.[overtimeRateField] ?? CONSTANTS.ZERO, { nonNullable: true })
        });
    }

    private clearRateRows(): void {
        this.ratesFormArray.clear({ emitEvent: false });
        this.overtimeRates.data = [];
    }

    private isLaborRowVisible(labor: string, showHideWhenWbsSelectedRows: boolean): boolean {
        if (HIDE_WHEN_WBS_SELECTED_LABORS.has(labor)) {
            return showHideWhenWbsSelectedRows;
        }

        if (WBS_OT_VISIBLE_LABORS.has(labor)) {
            return this.wbsOtRateVisible;
        }

        if (WBS_DDEP_VISIBLE_LABORS.has(labor)) {
            return this.wbsDdepOtRateVisible;
        }

        return true;
    }

    /** Rate cells are editable only while the override is on and the estimate is not read-only. */
    private applyRateRowsEditability(): void {
        
        for (const row of this.ratesFormArray.controls) {
            for (const column of Object.keys(OVERTIME_PERIOD_BY_COLUMN) as OvertimeRateColumn[]) {
                if (this.canEditOvertimeRates) {
                    row.controls[column].enable({ emitEvent: false });
                } else {
                    row.controls[column].disable({ emitEvent: false });
                }
            }
        }
    }

    private setVisibilityOfLaborForSalesOffice(salesOfficeId: number): void {
        const isDefaultEngOrZineOffice =
            salesOfficeId === this.estimateSettings.defaultEngOffice ||
            salesOfficeId === this.estimateSettings.defaultZineOffice;

        if (isDefaultEngOrZineOffice && !this.commmonDataService.globalPPMmode) {
            const isZineSelected = salesOfficeId === this.estimateSettings.zineOffice;
            this.wbsOtRateVisible = !isZineSelected;
            this.wbsDdepOtRateVisible = isZineSelected;
            return;
        }

        this.wbsOtRateVisible = this.commmonDataService.globalPPMmode;
        this.wbsDdepOtRateVisible = this.commmonDataService.globalPPMmode;
    }

    private shouldShowHideWhenWbsSelectedRows(): boolean {
        return (!this.wbsOtRateVisible && !this.wbsDdepOtRateVisible)
            || (this.wbsOtRateVisible && this.wbsDdepOtRateVisible && this.commmonDataService.globalPPMmode);
    }

    private buildOfficeNameLookup(): Record<number, string> {
        const officeNameLookup: Record<number, string> = {};

        this.solPropertiesService.getDeploymentOfficesMasterData().forEach(office => {
            officeNameLookup[office.salesofficeId] = office.salesOfficeName;
        });

        this.solPropertiesService.getEngineeringOfficesMasterData().forEach(office => {
            officeNameLookup[office.salesofficeId] = office.salesOfficeName;
        });

        this.solPropertiesService.getBusinessesAndSalesOfficesMasterData().forEach(office => {
            officeNameLookup[office.id] = office.branchName_City;
        });

        return officeNameLookup;
    }

    getSalesOfficeLaborData(){
        this.salesOfficeLaborRate = this.solPropertiesService.getSalesOfficeLaborRates();
        const officeNameLookup = this.buildOfficeNameLookup();

        this.salesOffices = this.salesOfficeLaborRate
        .filter(element => element.salesOfficeId > CONSTANTS.ZERO)
        .map(office => ({
            id: office.salesOfficeId,
            name: officeNameLookup[office.salesOfficeId] ?? CONSTANTS.EMPTY_STRING
        }));
    }

    trackById(index: number, office: SalesOffice): number {
        return office.id;
    }

    get isOverrideCalculationFormValue(): boolean {
            return this.overtimeRatesForm.controls.overrideCalculation?.value ?? false
    }

    get canEditOvertimeRates(): boolean {
        return !this.isReadOnly
            && this.isOverrideCalculationFormValue
            && this.overtimeRatesForm.controls.overrideCalculation.enabled;
    }
    
    onRateChange(rowIndex: number, column: OvertimeRateColumn): void {
        const salesOfficeId = this.overtimeRatesForm.controls.salesOffice.value;
        const row = this.ratesFormArray.at(rowIndex);

        if (salesOfficeId === null || salesOfficeId === undefined) {
            return;
        }

        const labor = row.controls.labor.value;
        const definition = OVERTIME_LABOR_DEFINITIONS.find(item => item.labor === labor);
        if (!definition) {
            return;
        }
        const value = row.controls[column].value;
        this.solPropertiesService.updateOvertimeRate(
            salesOfficeId,
            OVERTIME_PERIOD_BY_COLUMN[column],
            definition.overtimeRateField,
            Number.isFinite(value) ? Number(value) : CONSTANTS.ZERO
        );
        this.solPropertiesService.markPropertiesChanged();
    }
}
